import React, { useEffect, useState } from 'react';
import QRCode from 'qrcode.react';
import { Box, Button, Dialog, DialogTitle, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import usePreviewerDetection from '../../../../hooks/previewerDetection.hook';
import useAppInfo from '../../../../hooks/appInfo.hook';
import WaitingText from '../../../Onboarding/_shared/WaitingText';
import NumberedStep from '../../../Onboarding/_shared/NumberedStep';
import { buildPreviewerAppLink } from '../../../Onboarding/_shared/previewerAppLink';

const STEPS = [
    'Escaneie o QR Code com a câmera do celular.',
    'Instale o Visualizador Mobilizei pela loja de aplicativos.',
    'Abra o Visualizador. O celular aparece aqui automaticamente.'
];

// Long enough for the merchant to read "Conectando o celular…" before the dialog
// closes and the device shows up on the list.
const CONNECTING_DELAY_MS = 1200;

/**
 * QR code to pair a phone with the Visualizador. It waits for a device that opens
 * the store after `since` (the latest check-in already listed): a new phone, or a
 * listed one opened again, which the screen then shows as just connected.
 */
export default function PairPreviewerDialog({ open, isFirstDevice, since, onClose, onPaired }) {
    const { appInfo } = useAppInfo();
    const appLink = buildPreviewerAppLink(appInfo.platformId, appInfo.storeId);

    // waiting -> connecting (a new device was detected) -> the dialog closes.
    // paused: the hook gave up after a while with no new device.
    const [status, setStatus] = useState('waiting');

    useEffect(() => {
        if (open) setStatus('waiting');
    }, [open]);

    usePreviewerDetection({
        platformId: appInfo.platformId,
        storeId: appInfo.storeId,
        enabled: open && status === 'waiting',
        since,
        trackDetection: false,
        onDetected: (device) => {
            setStatus('connecting');
            setTimeout(() => onPaired(device), CONNECTING_DELAY_MS);
        },
        onStopped: () => setStatus('paused')
    });

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth={false} PaperProps={{ sx: { maxWidth: 640 } }}>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 20, fontWeight: 500 }}>
                {isFirstDevice ? 'Conectar um celular' : 'Parear outro celular'}
                <IconButton onClick={onClose} sx={{ mr: -1 }}>
                    <CloseIcon sx={{ color: 'rgba(0,0,0,.54)' }} />
                </IconButton>
            </DialogTitle>

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr auto' },
                    gap: 3,
                    px: 3,
                    pb: 3
                }}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Typography variant="body1">
                        {isFirstDevice
                            ? 'Instale o Visualizador Mobilizei no celular em que você quer receber as notificações de teste.'
                            : 'Use o celular em que você quer receber as notificações de teste.'
                        } Se ele já tiver o Visualizador, o QR Code abre o app e conecta a sua loja.
                    </Typography>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {STEPS.map((text, i) => (
                            <NumberedStep key={text} index={i + 1} fontSize={15}>{text}</NumberedStep>
                        ))}
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ opacity: status === 'connecting' ? 0.3 : 1, transition: 'opacity .3s' }}>
                        <QRCode value={appLink} size={180} />
                    </Box>

                    <Typography sx={{ fontSize: 14, color: 'rgba(0,0,0,.6)' }}>
                        {status === 'connecting' && <WaitingText>Conectando o celular</WaitingText>}
                        {status === 'waiting' && <WaitingText>Aguardando conexão</WaitingText>}
                    </Typography>

                    {status === 'paused' &&
                        <Button size="small" onClick={() => setStatus('waiting')}>
                            continuar aguardando
                        </Button>
                    }
                </Box>
            </Box>
        </Dialog>
    )
}
